// Partner portal: White-Label Branding Settings (Phase 6.6)
// Allows Gold+ partners to customize their branding (logo, colors, company name).
// Silver tier shows co-branding info. Starter/Bronze shows upgrade prompt.

import { useState, useEffect } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { Card, CardLabel, Button, Input, EmptyState, colors, headingStyle, mutedStyle, inputStyle as baseInputStyle, successTextStyle, pageWrapperStyle } from '../components/ui';
import { useLang } from '../i18n';

interface WhiteLabelConfig {
  id: string;
  partner_org_id: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  company_name: string | null;
  footer_text: string | null;
  email_from_name: string | null;
  enabled: boolean;
  tier_required: string;
}

export default function WhiteLabel() {
  const { t } = useLang();
  const [config, setConfig] = useState<WhiteLabelConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Form state
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0ea5e9');
  const [secondaryColor, setSecondaryColor] = useState('#67e8f9');
  const [companyName, setCompanyName] = useState('');
  const [footerText, setFooterText] = useState('');
  const [emailFromName, setEmailFromName] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    setLoading(true);
    try {
      const res = await fetch('/api/partner/white-label', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.config) {
          setConfig(data.config);
          setLogoUrl(data.config.logo_url ?? '');
          setPrimaryColor(data.config.primary_color ?? '#0ea5e9');
          setSecondaryColor(data.config.secondary_color ?? '#67e8f9');
          setCompanyName(data.config.company_name ?? '');
          setFooterText(data.config.footer_text ?? '');
          setEmailFromName(data.config.email_from_name ?? '');
        }
      }
    } catch {
      // fail-soft
    }
    setLoading(false);
  }

  async function saveConfig() {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/partner/white-label', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logo_url: logoUrl.trim() || null,
          primary_color: primaryColor || null,
          secondary_color: secondaryColor || null,
          company_name: companyName.trim() || null,
          footer_text: footerText.trim() || null,
          email_from_name: emailFromName.trim() || null,
        }),
      });
      if (res.ok) {
        setMessage(t.whiteLabel.saved);
        loadConfig();
      } else {
        const err = await res.json().catch(() => ({ error: 'save_failed' }));
        setMessage(`${t.common.error}: ${err.error ?? 'save_failed'}`);
      }
    } catch {
      setMessage(t.common.networkError);
    }
    setSaving(false);
  }

  if (loading) {
    return (
      <PortalLayout>
        <div style={{ padding: '2rem', color: colors.muted }}>{t.common.loading}</div>
      </PortalLayout>
    );
  }

  if (!config) {
    return (
      <PortalLayout>
        <div style={{ ...pageWrapperStyle, maxWidth: '600px' }}>
          <h1 style={{ ...headingStyle, marginBottom: '12px' }}>
            {t.whiteLabel.title}
          </h1>
          <EmptyState
            title={t.whiteLabel.title}
            description={t.whiteLabel.lockedDesc}
            action={
              <p style={{ ...mutedStyle, fontSize: '0.85rem', color: colors.mutedLight }}>
                {t.whiteLabel.lockedAction}
              </p>
            }
          />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div style={{ ...pageWrapperStyle, maxWidth: '700px' }}>
        <h1 style={{ ...headingStyle, marginBottom: '4px' }}>
          {t.whiteLabel.title}
        </h1>
        <p style={{ ...mutedStyle, marginBottom: '24px' }}>
          {t.whiteLabel.subtitle}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Logo URL */}
          <Input
            label={t.whiteLabel.fieldLogoUrl}
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder={t.whiteLabel.fieldLogoPlaceholder}
          />

          {/* Colors */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <CardLabel style={{ marginBottom: '4px' }}>{t.whiteLabel.fieldPrimaryColor}</CardLabel>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  style={{ width: '40px', height: '36px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  pattern="^#[0-9a-fA-F]{6}$"
                  style={{
                    ...baseInputStyle,
                    flex: 1,
                    marginBottom: 0,
                    fontFamily: 'monospace',
                  }}
                />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <CardLabel style={{ marginBottom: '4px' }}>{t.whiteLabel.fieldSecondaryColor}</CardLabel>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  style={{ width: '40px', height: '36px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  pattern="^#[0-9a-fA-F]{6}$"
                  style={{
                    ...baseInputStyle,
                    flex: 1,
                    marginBottom: 0,
                    fontFamily: 'monospace',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Company name */}
          <Input
            label={t.whiteLabel.fieldCompanyName}
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={t.whiteLabel.fieldCompanyPlaceholder}
            maxLength={100}
          />

          {/* Footer text */}
          <Input
            label={t.whiteLabel.fieldFooterText}
            type="text"
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            placeholder={t.whiteLabel.fieldFooterPlaceholder}
            maxLength={500}
          />

          {/* Email from name */}
          <Input
            label={t.whiteLabel.fieldEmailFromName}
            type="text"
            value={emailFromName}
            onChange={(e) => setEmailFromName(e.target.value)}
            placeholder={t.whiteLabel.fieldEmailFromPlaceholder}
            maxLength={100}
          />

          {/* Preview */}
          <Card style={{ background: colors.cardBgLight, marginTop: '8px' }}>
            <CardLabel style={{ color: colors.mutedLight, marginBottom: '8px' }}>
              {t.whiteLabel.previewTitle}
            </CardLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              {logoUrl ? (
                <img src={logoUrl} alt="Logo preview" style={{ height: '32px', maxWidth: '120px', objectFit: 'contain' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                  {(companyName || 'A').charAt(0).toUpperCase()}
                </div>
              )}
              <span style={{ fontSize: '1rem', fontWeight: 600, color: primaryColor }}>
                {companyName || t.whiteLabel.fieldCompanyPlaceholder}
              </span>
            </div>
            <div style={{ height: '4px', borderRadius: '2px', background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`, marginBottom: '12px' }} />
            <p style={{ fontSize: '0.8rem', color: colors.mutedLight }}>
              {footerText || t.whiteLabel.previewFooter}
            </p>
          </Card>

          <Button variant="primary" busy={saving} onClick={saveConfig} style={{ marginTop: '8px' }}>
            {t.whiteLabel.saveBtn}
          </Button>
          {message && (
            <p style={message.startsWith(t.common.error) ? { fontSize: '0.85rem', color: colors.dangerAlt } : successTextStyle}>
              {message}
            </p>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
